import 'intersection-observer';
import * as React from "react";
import { InView } from 'react-intersection-observer';
import { IOWidget, Updater, fromJSX } from "widgets-for-react";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";
import { ImageSize, Link } from "../types.gen";
import { getClassName, link } from "../utils.gen";
import { ImageContainer } from "./ImageContainer.gen";
import { CMSState } from '../../../publicSite/drupalPage/state'

export type LinkGridItem = {
  image: number
  image_size: ImageSize
  image_alt: string
  title: React.ReactNode
  label?: string
  intro: string
  link?: Link
  show_image: "yes" | "no"
}

export type LinkGridBlockProps = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: LinkGridItem[]
  link?: Link
  show_image: "yes" | "no",
  get_default_data_src:(() => ImageSize),
  get_default_data_srcset:(() => [ImageSize, string][])
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type LinkGridBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  list_classname: string
  grid_item_classname: string
  grid_article_classname: string
  grid_link_classname: string
  grid_header_classname: string
  grid_content_classname: string
  grid_title_classname: string
  grid_label_classname: string
  grid_intro_classname: string
  grid_fauxlink_classname: string
  grid_visual_classname: string
  grid_image_classname: string
  footer_classname: string
  clickable_classname: string
}>

// TODO: make gridItem separate component so that it looks and works the same way as expandBlock
export function LinkGridBlock(
  props: LinkGridBlockProps,
  options?: { style?: LinkGridBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return cmsState =>
    fromJSX(
      _ => {
        const gridItem = (item: LinkGridItem, index: number) => {
          return (
            <>
              {(item.title || item.label || item.intro || (item.link && item.link.title)) && (
                <div className={getClassName(options, "grid_content_classname", "card-body px-0 pb-0")}>
                  {(item.title || item.label) && (
                    <header className={getClassName(options, "grid_header_classname", "")}>
                      {item.label && (<span className={getClassName(options, "grid_label_classname", "mb-2")}>{item.label}</span>)}
                      {item.title && (<h3 className={getClassName(options, "grid_title_classname", "mb-2")}>{item.title}</h3>)}
                    </header>
                  )}
                  {item.intro && (<p className={getClassName(options, "grid_intro_classname", "")}>{item.intro}</p>)}
                  {item.link && item.link.title && (<span className={getClassName(options, "grid_fauxlink_classname", "btn btn-link pl-0")}>{item.link.title}</span>)}
                </div>
              )}
              {item.show_image === "yes" && item.image && cmsState.images.has(item.image)
                ? <div className={getClassName(options, "grid_visual_classname", "card-img-top")}>
                    {ImageContainer(
                      cmsState.images.get(item.image),
                      item.image_size,
                      getClassName(options, "grid_image_classname", "w-100"),
                      item.image_alt,
                      props.get_default_data_src,
                      props.get_default_data_srcset
                    ).run(_ => { })}
                  </div>
                : null
              }
            </>
          )
        }
        
        return (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('linkGrid', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={getClassName(options, "block_classname", "container")}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className={getClassName(options, "inner_classname", "py-5")}>
              {(props.title || props.intro) && (
                <header className={getClassName(options, "header_classname", "text-center")}>
                  {props.title && (<h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>)}
                  {props.intro && (<p className={getClassName(options, "intro_classname", "")}>{props.intro}</p>)}
                </header>
              )}

              <div className={getClassName(options, "content_classname", "")}>
                {props.items && (
                  <ul className={getClassName(options, "list_classname", "row list-unstyled mb-0")}>
                    {props.items.map((item: LinkGridItem, index: number) =>
                      <li className={getClassName(options, "grid_item_classname", "col-md-6 col-lg-4 py-3")} key={`grid-${options && options.key ? options.key : props.index}-${index}`}>
                        <article className={getClassName(options, "grid_article_classname", "")}>
                          {item.link
                            ? link(item.link,
                                  {classname: getClassName(options, "grid_link_classname", "d-flex flex-column-reverse text-secondary")},
                                  gridItem(item, index))
                            : <div className={getClassName(options, "grid_link_classname", "d-flex flex-column-reverse")}>{gridItem(item, index)}</div>
                          }
                        </article>
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {props.link &&
                <footer className={getClassName(options, "footer_classname", "text-center")}> 
                  {link(props.link,
                       {classname: getClassName(options, "clickable_classname", "btn btn-dark")},
                        props.link.title)}
                </footer>
              }
            </div>
          </InView>
      )},
    )
}
